import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { getErrorMessage } from '../utils/errorHandler';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Calendar, MapPin, Euro, Users, Clock, ArrowRight } from 'lucide-react';

const UTC_OFFSET_REGEX = /([zZ]|[+-]\d{2}:\d{2}|[+-]\d{4})$/;

const parseBackendUtcDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'number') {
    const parsedFromNumber = new Date(value);
    return Number.isNaN(parsedFromNumber.getTime()) ? null : parsedFromNumber;
  }

  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(' ', 'T');
  const utcCandidate = UTC_OFFSET_REGEX.test(normalized) ? normalized : `${normalized}Z`;
  const parsed = new Date(utcCandidate);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatUtcDateTime = (value) => {
  const parsed = parseBackendUtcDateTime(value);
  if (!parsed) return 'Data non disponibile';

  return parsed.toLocaleDateString('it-IT', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function PublicActivityRegistrationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // Activity data
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Registration flow steps
  const [step, setStep] = useState(1); // 1: Phone, 2: OTP, 3: Register/Login
  const [isMember, setIsMember] = useState(false);
  const [otpId, setOtpId] = useState(null);
  const [userId, setUserId] = useState(null);
  
  // Phone verification
  const [phonePrefix, setPhonePrefix] = useState('+39');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  
  // Registration form data
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    country_id: '',
    province_id: '',
    city_id: '',
    city_strgin: '',
    company_name: '',
    job_title: '',
    job_category_id: '',
    company_sector_id: '',
    linkedin_url: '',
    additional_info: '',
    privacy_accepted: false
  });
  
  // Lookup data
  const [countries, setCountries] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [companySectors, setCompanySectors] = useState([]);

  useEffect(() => {
    loadActivity();
    loadLookupData();
  }, [token]);

  const loadActivity = async () => {
    try {
      const response = await axios.get(`/public/activity/${token}`);
      setActivity(response.data);
      
      if (!response.data.registration_open) {
        toast.error('Le registrazioni per questo evento sono chiuse');
      }
    } catch (error) {
      toast.error('Evento non trovato');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadLookupData = async () => {
    try {
      const [countriesRes, categoriesRes, sectorsRes] = await Promise.all([
        axios.get('/public/countries'),
        axios.get('/public/job-categories'),
        axios.get('/public/company-sectors')
      ]);
      
      setCountries(countriesRes.data);
      setJobCategories(categoriesRes.data);
      setCompanySectors(sectorsRes.data);
    } catch (error) {
      console.error('Failed to load lookup data', error);
      toast.error('Errore nel caricamento dei dati');
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || phoneNumber.length < 8) {
      toast.error('Inserisci un numero di telefono valido');
      return;
    }
    
    try {
      await axios.post(`/public/activity/${token}/verify-phone`, {
        phone_prefix: phonePrefix,
        phone_number: phoneNumber
      });
      
      toast.success('Codice OTP inviato al tuo numero');
      setOtpId(null);
      setUserId(null);
      setStep(2);
    } catch (error) {
      toast.error('Errore nell\'invio dell\'OTP');
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Inserisci un codice OTP valido');
      return;
    }
    
    try {
      const response = await axios.post(`/public/activity/${token}/verify-otp`, {
        phone_prefix: phonePrefix,
        phone_number: phoneNumber,
        otp_code: otpCode
      });
      
      const verifyResult = response.data;
      setIsMember(verifyResult.is_member);
      
      if (verifyResult.is_member) {
        // Redirect to login with return URL
        toast.info('Effettua il login per completare la registrazione');
        navigate(`/login?redirect=/evento/${token}/registra&register=true`);
      } else {
        setOtpId(verifyResult.otp_id || null);
        setUserId(verifyResult.user_id || null);
        await prefillRegistrationForm(verifyResult);
        // Show registration form
        setStep(3);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Codice OTP non valido'));
    }
  };

  const handleCountryChange = async (countryId) => {
    setFormData((prev) => ({ ...prev, country_id: countryId, province_id: '', city_id: '' }));
  };

  const prefillRegistrationForm = async (data) => {
    const toStringOrEmpty = (value) => (value === null || value === undefined ? '' : String(value));

    setFormData((prev) => ({
      ...prev,
      email: data.email || '',
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      country_id: toStringOrEmpty(data.country_id),
      province_id: toStringOrEmpty(data.province_id),
      city_id: toStringOrEmpty(data.city_id),
      city_strgin: data.city_strgin || data.city || '',
      company_name: data.company_name || '',
      job_title: data.job_title || '',
      job_category_id: toStringOrEmpty(data.job_category_id),
      company_sector_id: toStringOrEmpty(data.company_sector_id),
      linkedin_url: data.linkedin_url || '',
      additional_info: data.additional_info || ''
    }));
  };

  const handleSubmitRegistration = async (e) => {
    e.preventDefault();
    
    if (!formData.privacy_accepted) {
      toast.error('Devi accettare l\'informativa sulla privacy');
      return;
    }
    
    const requiredTextFields = [
      formData.email,
      formData.first_name,
      formData.last_name,
      formData.city_strgin,
      formData.company_name,
      formData.job_title,
      formData.linkedin_url
    ];
    const hasMissingTextField = requiredTextFields.some((value) => !String(value || '').trim());
    const hasMissingSelectField = !formData.country_id || !formData.job_category_id || !formData.company_sector_id;

    if (hasMissingTextField || hasMissingSelectField) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    try {
      const response = await axios.post(`/public/activity/${token}/register-guest`, {
        phone_prefix: phonePrefix,
        phone_number: phoneNumber,
        otp_id: otpId,
        user_id: userId,
        ...formData
      });
      
      if (response.data.needs_payment) {
        if (!response.data.checkout_url) {
          throw new Error('Checkout URL not received');
        }

        window.location.href = response.data.checkout_url;
      } else {
        // Registration complete
        toast.success('Registrazione completata con successo!');
        navigate(`/evento/${token}/conferma`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella registrazione'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Activity Header */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">{activity.title}</CardTitle>
            <CardDescription className="text-base mt-2">{activity.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {activity.start_datetime && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>{formatUtcDateTime(activity.start_datetime)}</span>
                </div>
              )}
              {activity.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>{activity.location}</span>
                </div>
              )}
              {activity.price > 0 && (
                <div className="flex items-center gap-2">
                  <Euro className="h-4 w-4 text-gray-500" />
                  <span>€ {activity.price.toFixed(2)}</span>
                </div>
              )}
              {activity.max_participants && (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span>Max {activity.max_participants} partecipanti</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Registration Form */}
        {!activity.registration_open ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Registrazioni Chiuse</h3>
              <p className="text-gray-600">Le registrazioni per questo evento sono terminate.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Step 1: Phone Number */}
            {step === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>Verifica Numero di Telefono</CardTitle>
                  <CardDescription>Inserisci il tuo numero per ricevere il codice OTP</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSendOTP} className="space-y-4">
                    <div className="flex gap-2">
                      <div className="w-32">
                        <Label>Prefisso</Label>
                        <Select value={phonePrefix} onValueChange={setPhonePrefix}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="+39">+39 (IT)</SelectItem>
                            <SelectItem value="+1">+1 (US)</SelectItem>
                            <SelectItem value="+44">+44 (UK)</SelectItem>
                            <SelectItem value="+33">+33 (FR)</SelectItem>
                            <SelectItem value="+49">+49 (DE)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Label>Numero di Telefono *</Label>
                        <Input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder="3331234567"
                          required
                        />
                      </div>
                    </div>
                    
                    <Button type="submit" className="w-full">
                      Invia Codice OTP
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle>Inserisci Codice OTP</CardTitle>
                  <CardDescription>
                    Abbiamo inviato un codice al numero {phonePrefix}{phoneNumber}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleVerifyOTP} className="space-y-4">
                    <div>
                      <Label>Codice OTP (6 cifre) *</Label>
                      <Input
                        type="text"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        maxLength={6}
                        required
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                        Indietro
                      </Button>
                      <Button type="submit" className="flex-1">
                        Verifica Codice
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Registration Form (for non-members) */}
            {step === 3 && !isMember && (
              <Card>
                <CardHeader>
                  <CardTitle>Completa la registrazione</CardTitle>
                  <CardDescription>Inserisci i tuoi dati per completare l&apos;iscrizione</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitRegistration} className="space-y-6">
                    {/* Personal Info */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">Dati Personali</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Nome *</Label>
                          <Input
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>Cognome *</Label>
                          <Input
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">Località</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>
                            Nazione
                            <span> *</span>
                          </Label>
                          <Select value={formData.country_id} onValueChange={handleCountryChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona..." />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map(c => (
                                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label>
                            Città
                            <span> *</span>
                          </Label>
                          <Input
                            value={formData.city_strgin}
                            onChange={(e) => setFormData({ ...formData, city_strgin: e.target.value })}
                            placeholder="Inserisci città"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Professional Info */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">Dati Professionali</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Azienda *</Label>
                          <Input
                            value={formData.company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>Ruolo *</Label>
                          <Input
                            value={formData.job_title}
                            onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Categoria professionale *</Label>
                          <Select 
                            value={formData.job_category_id} 
                            onValueChange={(val) => setFormData({ ...formData, job_category_id: val })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona..." />
                            </SelectTrigger>
                            <SelectContent>
                              {jobCategories.map(cat => (
                                <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label>Settore aziendale *</Label>
                          <Select 
                            value={formData.company_sector_id} 
                            onValueChange={(val) => setFormData({ ...formData, company_sector_id: val })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona..." />
                            </SelectTrigger>
                            <SelectContent>
                              {companySectors.map(sector => (
                                <SelectItem key={sector.id} value={String(sector.id)}>{sector.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label>LinkedIn *</Label>
                        <Input
                          type="url"
                          value={formData.linkedin_url}
                          onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                          placeholder="https://www.linkedin.com/in/..."
                          required
                        />
                      </div>
                      
                      <div>
                        <Label>Altro</Label>
                        <Input
                          value={formData.additional_info}
                          onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Privacy */}
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="privacy"
                        checked={formData.privacy_accepted}
                        onCheckedChange={(checked) => setFormData({ ...formData, privacy_accepted: checked })}
                      />
                      <label htmlFor="privacy" className="text-sm text-gray-600 cursor-pointer">
                        Accetto l&apos;
                        <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">
                          informativa sulla privacy
                        </a> *
                      </label>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1">
                        Indietro
                      </Button>
                      <Button type="submit" className="flex-1">
                        Completa registrazione
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
